import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { LowerRole } from '@shared/types';

import {
  agencyDeliveryDashboardDataAtom,
  agencyDeliveryDashboardStatusAtom,
  agencyDeliveryStatisticsDataAtom,
  agencyDeliveryStatisticsStatusAtom,
  deliveryDashboardDataAtom,
  deliveryDashboardStatusAtom,
  giftDeliverySearchDataAtomFamily,
  giftDeliverySearchStatusAtomFamily,
  giftDeliveryStatisticsDataAtom,
  giftDeliveryStatisticsStatusAtom,
} from './giftDelivery.atom';
import { GiftDeliverySearchParams, GiftDeliveryStatus } from './giftDelivery.interface';

const giftDeliveryPopulate = [
  {
    path: 'giftItem',
    populate: [{ path: 'image' }, { path: 'giftItemInfo' }],
  },
  {
    path: 'giftOrder',
    select:
      'receiverName receiverPhoneNumber deliveryMode paymentDetail.purchasedAt bugo',
    populate: [
      {
        path: 'bugo',
        select: 'member',
        populate: [{ path: 'member', select: 'fullName phoneNumber' }],
      },
    ],
  },
  {
    path: 'shipping.deliveredImage',
    select: 'url',
  },
];

// 배송 대시보드 (주소대기 + 주소입력완료 + 준비중 + 배송중 + 오늘 배송완료)
export const deliveryDashboardUrl = `/${LowerRole.Admin}/gift-delivery/dashboard`;

export const useDeliveryDashboardHook = (skip = false) => {
  useRecoilQuery(
    deliveryDashboardStatusAtom,
    deliveryDashboardDataAtom,
    deliveryDashboardUrl,
    fetcher,
    skip,
  );
};

// 배송 통계
export const giftDeliveryStatisticsUrl = `/${LowerRole.Admin}/gift-delivery/statistics`;

export const useGiftDeliveryStatisticsHook = (skip = false) => {
  useRecoilQuery(
    giftDeliveryStatisticsStatusAtom,
    giftDeliveryStatisticsDataAtom,
    giftDeliveryStatisticsUrl,
    fetcher,
    skip,
  );
};

// 배송 검색 (Admin) - 내역 검색 페이지용
const giftDeliverySearchUrl = (params: GiftDeliverySearchParams) => {
  const filter: Record<string, unknown> = {};

  if (params.deliveryStatus) {
    filter.deliveryStatus = params.deliveryStatus;
  }

  if (params.type) {
    filter.type = params.type;
  }

  if (params.recipientPhone) {
    filter['recipient.phoneNumber'] = { $regex: params.recipientPhone };
  }

  if (params.recipientName) {
    filter['recipient.name'] = { $regex: params.recipientName };
  }

  if (params.dateFrom || params.dateTo) {
    const dateFilter: Record<string, string> = {};
    if (params.dateFrom) dateFilter.$gte = new Date(params.dateFrom).toISOString();
    if (params.dateTo) dateFilter.$lte = new Date(params.dateTo).toISOString();
    filter.createdAt = dateFilter;
  }

  return `/${LowerRole.Admin}/gift-delivery/search?${queryString({
    filter,
    populate: giftDeliveryPopulate,
    options: { sort: { createdAt: -1 } },
    ...(params.ordererName ? { ordererName: params.ordererName } : {}),
  })}`;
};

export const useGiftDeliverySearchHook = (
  searchKey: string,
  params: GiftDeliverySearchParams,
  skip = false,
) => {
  useRecoilQuery(
    giftDeliverySearchStatusAtomFamily(searchKey),
    giftDeliverySearchDataAtomFamily(searchKey),
    giftDeliverySearchUrl(params),
    fetcher,
    skip,
  );
};

// 배송완료 전체 내역 (대시보드의 todayDelivered는 오늘분만 내려주므로, 배송완료 필터 선택 시
// 권한 스코프가 적용된 제네릭 조회로 전체 배송완료를 가져온다. Admin/BugoAgencyAdmin 공용 —
// 대행사는 백엔드 owningFilter로 자기 브랜드 배송완료만 반환된다.)
export const giftDeliveryDeliveredKey = (
  role: LowerRole.Admin | LowerRole.BugoAgencyAdmin,
) => `delivered:${role}`;

export const giftDeliveryDeliveredUrl = (
  role: LowerRole.Admin | LowerRole.BugoAgencyAdmin,
) =>
  `/${role}/gift-delivery?${queryString({
    filter: { deliveryStatus: GiftDeliveryStatus.Delivered },
    populate: giftDeliveryPopulate,
    options: { sort: { 'shipping.deliveredAt': -1 } },
  })}`;

export const useGiftDeliveryDeliveredHook = (
  role: LowerRole.Admin | LowerRole.BugoAgencyAdmin,
  skip = false,
) => {
  const key = giftDeliveryDeliveredKey(role);
  useRecoilQuery(
    giftDeliverySearchStatusAtomFamily(key),
    giftDeliverySearchDataAtomFamily(key),
    giftDeliveryDeliveredUrl(role),
    fetcher,
    skip,
  );
};

// ===== BugoAgencyAdmin =====

export const agencyDeliveryDashboardUrl = `/${LowerRole.BugoAgencyAdmin}/gift-delivery/dashboard`;

export const useAgencyDeliveryDashboardHook = (skip = false) => {
  useRecoilQuery(
    agencyDeliveryDashboardStatusAtom,
    agencyDeliveryDashboardDataAtom,
    agencyDeliveryDashboardUrl,
    fetcher,
    skip,
  );
};

export const agencyDeliveryStatisticsUrl = `/${LowerRole.BugoAgencyAdmin}/gift-delivery/statistics`;

export const useAgencyDeliveryStatisticsHook = (skip = false) => {
  useRecoilQuery(
    agencyDeliveryStatisticsStatusAtom,
    agencyDeliveryStatisticsDataAtom,
    agencyDeliveryStatisticsUrl,
    fetcher,
    skip,
  );
};
